import type {TreeValuesResponse} from "../ApiTypes";
import type {L1To8} from "../ApiHelpers";
import type {PpvOpportunity} from "./OpportunityClasses";
import type {Supplier} from "../../pages/supplier-normalization/pages/SupplierNormalization.classes";
import type {ApiCategory} from "./Category.ts";
import type {Optional} from "../../utils/ts-utils.ts";

export type Decimal = number // Although we receive a string from the API

// Backend classes

export enum ReviewChoice {
    ACCEPT,
    REJECT,
}

export type Categories = string[]

export enum MatPartReviewRowState {
    input = 'input',
    ai = 'ai',
    reviewed = 'reviewed',
    pending = 'pending',
    approved = 'approved',
    rejected = 'rejected',
    inconsistent = 'inconsistent',
}

export const VALID_MAT_PART_REVIEW_ROW_STATES = [
    MatPartReviewRowState.input,
    MatPartReviewRowState.ai,
    MatPartReviewRowState.reviewed,
    MatPartReviewRowState.pending,
    MatPartReviewRowState.approved,
    MatPartReviewRowState.rejected,
];

export function MatPartReviewRowStateToString(s: MatPartReviewRowState): string {
    switch (s) {
        case MatPartReviewRowState.input:
            return 'Input';
        case MatPartReviewRowState.ai:
            return 'Ai';
        case MatPartReviewRowState.reviewed:
            return 'Reviewed';
        case MatPartReviewRowState.pending:
            return 'Pending';
        case MatPartReviewRowState.approved:
            return 'Accepted';
        case MatPartReviewRowState.rejected:
            return 'Rejected';
        case MatPartReviewRowState.inconsistent:
            break;
    }
    return '?'
}

export type MatPartReviewRowResp = {
    // Field id is not in API, but is being used in MUIX internal types
    id: string
    databag: number
    revision_id: number
    // State
    state: '' | MatPartReviewRowState
    // Part
    part: number
    related_part?: Partial<PartData>
    p_id: string
    p_spend: Decimal
    // Supplier
    suppler: number
    related_supplier?: Partial<Supplier>
    s_name: string
    // Business unit
    business_unit: number
    related_business_unit?: Partial<BusinessUnit>
    // Category
    cat: Optional<ApiCategory, 'id'>
    previous_cat: Optional<ApiCategory, 'id'>
    // AI fields
    suggestion_score: number
    suggestion_modified: string
    reasoning: string
    // Review
    review_choice: ReviewChoice | null
    review_user_id: number | null
    review_modified: string
    review_notes: string
    // Approval
    approval: number | null
    approval_applied: boolean
    feedback_choice: ReviewChoice | null
    feedback_user_id: number | null
}
// TODO CAT-2731: Get this with an operator / function like keyof
export const keyOfMatPartReviewRowResp = [
    'id',
    'databag',
    'revision_id',
    'state',
    'part',
    'related_part',
    'p_id',
    'p_spend',
    'suppler',
    'related_supplier',
    's_name',
    'business_unit',
    'related_business_unit',
    'cat',
    'previous_cat',
    'suggestion_score',
    'suggestion_modified',
    'reasoning',
    'review_choice',
    'review_user_id',
    'review_modified',
    'review_notes',
    'approval',
    'approval_applied',
    'feedback_choice',
    'feedback_user_id',
]

/**
 * Flattened version of MatPartReviewRowResp
 */
export type MatPartReviewRow =
    MatPartReviewRowResp
    & Partial<Omit<PartData, 'id'>>
    & Partial<Omit<Supplier, 'id'>>
    & Partial<Omit<BusinessUnit, 'id'>>
    & {supplier_id: number} // Needed for grouping

export type MatReviewAggregatedGroupData = {
    count: number
    p_spend_agg: string
    cat_id_agg: number[]
    state_agg: MatPartReviewRowState[]
    n_pending_agg: number
    n_is_open_uncat_agg: number

    cat_common_l1_agg: string[]
    cat_common_l2_agg: string[]
    cat_common_l3_agg: string[]
    cat_common_l4_agg: string[]
    cat_common_l5_agg: string[]
    cat_common_l6_agg: string[]
    cat_common_l7_agg: string[]
    cat_common_l8_agg: string[]

    bu_id_agg: string[]
    bu_name_agg: string[]
}

export type MatPartReviewRowWithContext = MatPartReviewRow & {
    context: {[l in L1To8]: string}
}

export type MatReviewStatisticsSerializer = {
    databag_id: number
    total_spend: Decimal
    total_parts: number
    cat_new_spend: Decimal
    cat_new_parts: number
    cat_same_spend: Decimal
    cat_same_parts: number
    cat_recat_spend: Decimal
    cat_recat_parts: number
}
export type MatReviewLevelStatisticsTreeSerializer = TreeValuesResponse<{
    pre_spend: number
    post_spend: number
    pre_parts: number
    post_parts: number
    pre_suppliers: number
    post_suppliers: number
    pre_uncategorized_spend: number | undefined // Can be undefined in old backend versions
    post_uncategorized_spend: number | undefined // Can be undefined in old backend versions
    pre_uncategorized_parts: number | undefined // Can be undefined in old backend versions
    post_uncategorized_parts: number | undefined // Can be undefined in old backend versions
}>

export type MatReviewSampleStatistic = {
    sample_target_size: number
    sample_review_progress: {
        reviewed: number
        total: number
    }
}

export type PpvLevelStatisticsTreeSerializer = TreeValuesResponse<{
    n_suppliers?: number
    total_spend?: number
    total_ppv_opportunity: number
}>


export type MatConcentrationStatistics = {
    databag: number
    total_parts: number
    total_spend: Decimal
    total_suppliers: number
    top_n_percentage: number
    top_n_spend: Decimal
    top_n_spend_percentage: Decimal
}

export type MatCategoryConcentrationStatistics = {
    id: number
    level: number
    l1: string
    l2: string
    l3: string
    l4: string
    l5: string
    l6: string
    l7: string
    l8: string
    databag: number

    total_spend: Decimal
    distinct_suppliers: number
    top_n_percentage: number
    top_n_spend: Decimal
    top_n_spend_percentage: number
}

export type MatSupplierCategoryConcentrationStatistics = {
    id: number
    level: number
    l1: string
    l2: string
    l3: string
    l4: string
    l5: string
    l6: string
    l7: string
    l8: string
    databag: number

    supplier_id: number
    s_id: string
    s_name: string

    s_c_spend: Decimal
    s_c_cumulative_spend: number
    s_c_cumulative_spend_percentage: number
    s_total_spend: Decimal
    s_total_cumulative_spend: number
    s_total_cumulative_spend_percentage: number
}

export type MReviewSpencConcentrationCategory = {
    id: string
    group: string
    count: number
    p_spend_agg: number
    distinct_suppliers: number
}

export type MReviewSpencConcentrationSupplier = {
    id: string
    group: string
    count: number
    p_spend_agg: number
}

export type PartData = {
    p_id: string
    p_name: string
    p_description: string
    p_spend: Decimal
    p_context_1: string
    p_context_2: string
    p_context_3: string
    p_date_1: string
    p_date_2: string
    p_date_3: string
    p_ddate_1: string
    p_ddate_2: string
    p_ddate_3: string
    file_name: string
}

export type BusinessUnit = {
    id: number
    databag_id: number
    bu_id: string
    bu_name: string
    bu_group_d0_id?: string
    bu_group_d0_name?: string
    bu_group_d1_id?: string
    bu_group_d1_name?: string
    bu_group_d2_id?: string
    bu_group_d2_name?: string
}

export type ApplyTaxonomyMappingRequestData = {
    src_mat_taxonomy: number
    dst_mat_taxonomy: number
    map_src_classification_databag: number
    map_dst_classification_databag: number
}

export type MaterializeRequest = {
    databag: number
    clear: boolean
}


export type PpvGroup = {
    p_name: string // ppv_key
    ppv_opportunity: string
    opportunity?: PpvOpportunity | null
}

export type PpvPart = {
    id: number
    p_id: string
    p_name: string
    p_description: string
    p_spend: Decimal
    p_quantity: number
    p_ddate_1: string
    supplier_id: number
    s_name: string
    s_country: string
    bu_name: string
    bu_country: string
    group_key: string,
    l1: string,
    l2: string,
    l3: string,
    avg_unit_cost: number
    variance_cost: number
    ppv_delta_perc: number
    ppv_opportunity: number
    unit_cost_min: number
    unit_cost_max: number
    ppv_group: number
    part: number,
    combined_quantity: number
    combined_spend: number
    combined_unit_cost: number
    n_parts: number
    n_suppliers: number
    cc_description: string
    p_incoterms: string
    p_incoterms_location: string
    latest_spend: string
    latest_quantity: number
    latest_unit_cost: number
}

export type PpvStatistic = {
    id: number
    databag: number
    level: number
    l1: string
    l2: string
    l3: string
    l4: string
    l5: string
    l6: string
    l7: string
    l8: string

    total_ppv_opportunity: number
}

export type PpvGroupRequest = {
    databag?: number

    /**
     * Filter on PPV Groups by searching the group_key field
     */
    search?: string

    /**
     * Which field to order the group results by (ascending, or `-field` for descending)
     */
    ordering?: `${keyof PpvGroup}` | `-${keyof PpvGroup}`
    business_unit?: number | null | undefined
    cc_description?: string //Entity / Site
    level: number
    l1?: string
    l2?: string
    l3?: string
    l4?: string
    l5?: string
    l6?: string
    l7?: string
    l8?: string

    startDate?: string //start date
    endDate?: string //end date

    page_size?: number
    page?: number
}


